import { useState, useEffect } from 'react';
import {
  ProjectAPI, 
  Project, 
  CreateProjectData, 
  UpdateProjectData,
  FindProjectsOptions,
  ProjectSecurityMetrics,
  ProjectLicenseDistribution,
  Pagination
} from '../api';

export function useProjects(options?: FindProjectsOptions) {
  const [data, setData] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>();

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await ProjectAPI.getAll(options);
      
      if (response.code === '200' && response.data) {
         setData(response.data as any);

         setPagination({
           totalItems: response.totalNoOfItem || 0,
           page: response.page || 1,
           perPage: response.perPage || 10,
         });
      } else {
        setError(response.message || 'Failed to fetch projects');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [options?.workgroupId, options?.applicationId]);

  return { 
    data, 
    loading, 
    error, 
    pagination,
    refetch: fetchProjects 
  };
}

export function useProject(id: number) {
  const [data, setData] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await ProjectAPI.getById(id);

      if (response.code === '200' && response.data) {
        setData(response.data);
      } else {
        setError(response.message || 'Failed to fetch project');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchProject();
    }
  }, [id]);

  return { data, loading, error, refetch: fetchProject };
}

export function useProjectList(workgroupId: number, applicationId?: number) {
  const [data, setData] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await ProjectAPI.getList(applicationId);

      if (response.code === '200' && response.data) {
        setData(response.data);
      } else {
        setError(response.message || 'Failed to fetch project');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workgroupId || applicationId) {
      fetchProjects();
    }
  }, [workgroupId, applicationId]);

  return { data, loading, error, refetch: fetchProjects };
}

export function useProjectMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createProject = async (data: CreateProjectData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ProjectAPI.create(data);
      
      if (response.code === '201' || response.code === '200') {
        return response.data;
      } else {
        setError(response.message || 'Failed to create project');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async (id: number, data: UpdateProjectData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ProjectAPI.update(id, data);
      
      if (response.code === '200') {
        return response.data;
      } else {
        setError(response.message || 'Failed to update project');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ProjectAPI.delete(id);
      
      if (response.code === '200') {
        return true;
      } else {
        setError(response.message || 'Failed to delete project');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    createProject,
    updateProject,
    deleteProject,
    loading,
    error,
  };
}
